import React, { type RefObject, Suspense, useMemo, useState } from 'react';

import { ErrorBoundary } from 'react-error-boundary';
import { graphql, useLazyLoadQuery } from 'react-relay';

import { cssMap } from '@atlaskit/css';
import { useResizingHeight } from '@atlaskit/motion';
import { Box, Flex } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { useAudioPlayerStreamingContext } from '../../context/AudioPlayerStreamingProvider';
import { useAudioPlayerStore } from '../../hooks/useAudioPlayerStore';
import { setActiveContent } from '../../state/AudioPlayerState';
import { MinifiedAudioPlayer } from '../audio-player/MinifiedAudioPlayer';

import type { PlaylistQuery as PlaylistQueryType } from './__generated__/PlaylistQuery.graphql';
import { PlaylistEmptyView } from './PlaylistEmptyView';
import { PlaylistErrorView } from './PlaylistErrorView';
import { PlaylistHeader } from './PlaylistHeader';
import { type PlaylistItemData } from './PlaylistItem';
import { PlaylistLoadingView } from './PlaylistLoadingView';
import { PlaylistView } from './PlaylistView';

const styles = cssMap({
	wrapper: {
		borderRadius: token('radius.large'),
		backgroundColor: token('elevation.surface'),
		boxShadow: token('elevation.shadow.overflow'),
		position: 'absolute',
		bottom: token('space.100'),
		left: token('space.100'),
		right: token('space.100'),
		display: 'flex',
		flexDirection: 'column',
		overflow: 'hidden',
	},
	contentContainer: {
		height: '406px',
		display: 'flex',
		flexDirection: 'column',
		overflowY: 'auto',
		position: 'relative',
	},
	fullHeightWrapper: {
		flexGrow: 1,
	},
	coverBorder: {
		position: 'absolute',
		width: '100%',
		top: '0',
		backgroundColor: token('elevation.surface'),
		zIndex: 200,
		height: '12px',
	},
	stickyShadow: {
		position: 'sticky',
		top: token('space.negative.050'),
		zIndex: 1,
		boxShadow: token('elevation.shadow.overflow'),
		backgroundColor: token('elevation.surface'),
		height: '5px',
	},
	minifiedAudioPlayer: {
		marginLeft: token('space.100'),
		marginRight: token('space.100'),
		marginTop: token('space.100'),
		marginBottom: token('space.100'),
	},
});

type PlaylistWrapperProps = {
	children: React.ReactNode;
	defaultExpanded?: boolean;
	hasOtherActivePlayback?: boolean;
	audioRef: RefObject<HTMLAudioElement>;
};

const PlaylistWrapper = ({
	children,
	defaultExpanded = false,
	hasOtherActivePlayback = false,
	audioRef,
}: PlaylistWrapperProps) => {
	const resizingHeightProps = useResizingHeight();
	const [isExpanded, setIsExpanded] = useState(defaultExpanded);
	const { activeContent } = useAudioPlayerStore();

	const handleToggle = () => setIsExpanded((prev: boolean) => !prev);

	const shouldShowMinifiedPlayer = !isExpanded && hasOtherActivePlayback;

	return (
		<Flex direction="column" xcss={styles.wrapper}>
			<PlaylistHeader isExpanded={isExpanded} onToggle={handleToggle} />
			{shouldShowMinifiedPlayer && (
				<Box xcss={styles.minifiedAudioPlayer}>
					<MinifiedAudioPlayer
						title={activeContent.title || ''}
						byline=""
						avatarUrl={''}
						elevated
						audioRef={audioRef}
					/>
				</Box>
			)}
			<div {...resizingHeightProps}>
				{isExpanded && (
					<Flex xcss={styles.contentContainer}>
						{/* Scroll shadow effect */}
						<Box xcss={styles.coverBorder} />
						<Flex direction="column" xcss={styles.fullHeightWrapper}>
							<Box xcss={styles.stickyShadow} />
							{children}
						</Flex>
					</Flex>
				)}
			</div>
		</Flex>
	);
};

type PlaylistInnerProps = {
	cloudId: string;
	audioRef: RefObject<HTMLAudioElement>;
};

const PlaylistInner = ({ cloudId, audioRef }: PlaylistInnerProps) => {
	const { fetchAudio } = useAudioPlayerStreamingContext();
	const { activeContent } = useAudioPlayerStore();

	const data = useLazyLoadQuery<PlaylistQueryType>(
		graphql`
			query PlaylistQuery($cloudId: ID!) {
				confluence_getPlaylist(cloudId: $cloudId) {
					tracks {
						nodes {
							id
							type
							mainContent {
								id
								title
								type
								subType
								space {
									key
								}
								history {
									createdBy {
										displayName
									}
								}
								properties(keys: ["emoji-title-published"]) {
									nodes {
										value
									}
								}
							}
						}
					}
				}
			}
		`,
		{ cloudId },
		{
			fetchPolicy: 'store-or-network',
		},
	);

	const playlistItems: PlaylistItemData[] = useMemo(() => {
		const tracks = (data as any).confluence_getPlaylist?.tracks?.nodes || [];
		return tracks.map((track: any) => {
			const mainContent = track.mainContent;
			const creator = mainContent?.history?.createdBy;

			return {
				id: mainContent?.id, // Use mainContent.id as the content ID
				title: mainContent?.title || undefined,
				type: track.type,
				author: creator?.displayName || undefined,
				spaceKey: mainContent?.space?.key || '',
				contentType: mainContent?.type || '',
				isLive: !!(mainContent?.subType === 'live'),
				emojiId: mainContent?.properties?.nodes?.[0]?.value || undefined,
			};
		});
	}, [data]);

	const currentItem = useMemo(() => {
		if (!activeContent?.contentId) {
			return null;
		}
		return playlistItems.find((item) => item.id === activeContent.contentId) || null;
	}, [activeContent?.contentId, playlistItems]);

	const handleItemClick = (item: PlaylistItemData) => {
		setActiveContent({
			contentId: item.id,
			spaceKey: item.spaceKey || '',
			contentType: item.contentType || '',
			title: item.title,
			author: item.author,
			avatarUrl: '', // TODO: Add avatar URL when available
		});
		fetchAudio();
	};

	return (
		<Flex direction="column">
			{playlistItems.length === 0 ? (
				<PlaylistEmptyView />
			) : (
				<PlaylistView
					items={playlistItems}
					currentItem={currentItem}
					onItemClick={handleItemClick}
					audioRef={audioRef}
				/>
			)}
		</Flex>
	);
};

type PlaylistProps = {
	cloudId: string;
	defaultExpanded?: boolean;
	audioRef: RefObject<HTMLAudioElement>;
};

export const Playlist = ({ cloudId, defaultExpanded, audioRef }: PlaylistProps) => {
	const { pendingContent } = useAudioPlayerStore();

	return (
		<PlaylistWrapper
			defaultExpanded={defaultExpanded}
			hasOtherActivePlayback={pendingContent !== null}
			audioRef={audioRef}
		>
			<ErrorBoundary
				fallbackRender={(props) => <PlaylistErrorView {...props} onRetry={() => {}} />}
			>
				<Suspense fallback={<PlaylistLoadingView />}>
					<PlaylistInner cloudId={cloudId} audioRef={audioRef} />
				</Suspense>
			</ErrorBoundary>
		</PlaylistWrapper>
	);
};
